/**
 * Поля «Услуга» и «Итог звонка» карточки звонка: названия и видимость из настроек тенанта
 * (`GET /settings/call-card-fields`).
 *
 * Значения полей приходят в самом звонке (`nodeServiceLabel`, `nodeCallOutcome*`): из какого
 * выхода пакета их брать, решает сервер. Здесь — только как поле называется и показывать ли
 * его. Чистым модулем, а не внутри контекста, по той же причине, что `panelSettingsState.js`:
 * компоненты юнит-тестами не покрываются, а разбор ответа и умолчания проверять надо.
 */

export const CALL_CARD_FIELD_SLOTS = Object.freeze({
  SERVICE: 'service',
  OUTCOME: 'outcome'
});

/** Порядок полей в карточке и на экране настроек. */
export const CALL_CARD_FIELD_ORDER = Object.freeze([CALL_CARD_FIELD_SLOTS.SERVICE, CALL_CARD_FIELD_SLOTS.OUTCOME]);

/**
 * Названия по умолчанию. Единственное место, где они записаны: карточка и журнал берут их
 * отсюда, а сторож в тесте следит, чтобы литералы не вернулись в разметку.
 */
export const DEFAULT_CALL_CARD_FIELD_TITLES = Object.freeze({
  [CALL_CARD_FIELD_SLOTS.SERVICE]: 'Услуга',
  [CALL_CARD_FIELD_SLOTS.OUTCOME]: 'Итог звонка'
});

const DEFAULT_FIELD = Object.freeze({
  title: null,
  visible: true,
  source: null,
  defaultSource: null,
  effectiveSource: null,
  sourceFound: true
});

export const EMPTY_CALL_CARD_FIELDS = Object.freeze({
  fields: Object.freeze(
    Object.fromEntries(CALL_CARD_FIELD_ORDER.map((slot) => [slot, DEFAULT_FIELD]))
  ),
  candidates: Object.freeze([])
});

const nonEmptyString = (value) => (typeof value === 'string' && value.trim() !== '' ? value : null);

const isPlainObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);

const normalizeField = (field) => {
  if (!isPlainObject(field)) {
    return DEFAULT_FIELD;
  }

  return {
    title: nonEmptyString(field.title),
    // Видимо всё, кроме явного false: fail-open, как у панелей интерфейса.
    visible: field.visible !== false,
    source: nonEmptyString(field.source),
    defaultSource: nonEmptyString(field.defaultSource),
    effectiveSource: nonEmptyString(field.effectiveSource),
    sourceFound: field.sourceFound !== false
  };
};

const normalizeCandidate = (candidate) => {
  const path = nonEmptyString(candidate?.path);
  if (!isPlainObject(candidate) || !path) {
    return null;
  }

  return {
    path,
    nodeId: nonEmptyString(candidate.nodeId) ?? path.split('.')[0],
    nodeTitle: nonEmptyString(candidate.nodeTitle) ?? nonEmptyString(candidate.nodeId) ?? path,
    title: nonEmptyString(candidate.title) ?? path,
    kind: nonEmptyString(candidate.kind)
  };
};

/**
 * Ответ сервера → состояние. Всё, что не похоже на настройку, отбрасывается: фронтенд новее или
 * старше бэкенда не должен ронять карточку звонка из-за лишнего или пропавшего поля.
 */
export const normalizeCallCardFields = (payload) => {
  const fields = isPlainObject(payload?.fields) ? payload.fields : {};
  const candidates = Array.isArray(payload?.candidates) ? payload.candidates : [];

  return {
    fields: Object.fromEntries(CALL_CARD_FIELD_ORDER.map((slot) => [slot, normalizeField(fields[slot])])),
    candidates: candidates.map(normalizeCandidate).filter(Boolean)
  };
};

/** Название поля: своё из настроек, иначе умолчание. */
export const resolveFieldTitle = (state, slot) =>
  nonEmptyString(state?.fields?.[slot]?.title) ?? DEFAULT_CALL_CARD_FIELD_TITLES[slot] ?? '';

export const isFieldVisible = (state, slot) => state?.fields?.[slot]?.visible !== false;

/**
 * Число колонок журнала «Детали звонков». Строки «Загружаем…» и «Нет записей» растягиваются
 * на всю таблицу — со скрытым «Итогом звонка» колонок на одну меньше.
 */
export const JOURNAL_COLUMN_COUNT = 10;

export const journalColumnCount = ({ outcomeVisible }) =>
  outcomeVisible ? JOURNAL_COLUMN_COUNT : JOURNAL_COLUMN_COUNT - 1;
